// Error boundary for workspace operations

import React, { Component, ReactNode } from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

interface WorkspaceErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface WorkspaceErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

/**
 * Error boundary specifically for workspace-related operations
 * Provides graceful degradation and recovery options
 */
export class WorkspaceErrorBoundary extends Component<
  WorkspaceErrorBoundaryProps,
  WorkspaceErrorBoundaryState
> {
  constructor(props: WorkspaceErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<WorkspaceErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Call the onError callback if provided
    this.props.onError?.(error, errorInfo);

    // Log the error (in production, this might go to an error reporting service)
    console.error('Workspace Error:', error);
    console.error('Error Info:', errorInfo);
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleClearWorkspace = () => {
    // Clear any corrupted workspace data from localStorage
    try {
      const workspaceKeys = Object.keys(localStorage).filter((key) =>
        key.startsWith('weibull_workspace')
      );
      workspaceKeys.forEach((key) => localStorage.removeItem(key));
    } catch (e) {
      console.warn('Failed to clear workspace data:', e);
    }

    this.handleRetry();
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-[400px] flex items-center justify-center bg-gray-50 rounded-lg">
          <div className="max-w-md w-full p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-red-100 rounded-full">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
            </div>

            <h3 className="text-lg font-semibold text-gray-900 mb-2">Workspace Error</h3>

            <p className="text-gray-600 mb-6">{this.getErrorMessage()}</p>

            <div className="space-y-3">
              <button
                onClick={this.handleRetry}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <ArrowPathIcon className="w-4 h-4" />
                <span>Try Again</span>
              </button>

              <button
                onClick={this.handleClearWorkspace}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Clear Workspace Data
              </button>

              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2 text-gray-500 hover:text-gray-700 transition-colors text-sm"
              >
                Reload Page
              </button>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="text-sm text-gray-500 cursor-pointer">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 rounded text-xs text-gray-700 overflow-auto">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }

  private getErrorMessage(): string {
    const error = this.state.error;

    if (!error) return 'An unexpected error occurred.';

    // Provide user-friendly messages for common workspace errors
    if (error.message.includes('workspace')) {
      return 'There was a problem loading or saving your workspace. Your data may be corrupted.';
    }

    if (error.message.includes('localStorage')) {
      return 'Unable to access browser storage. Your browser may be in private mode or storage may be full.';
    }

    if (error.message.includes('network') || error.message.includes('fetch')) {
      return 'Network error occurred while accessing workspace data. Please check your connection.';
    }

    if (error.message.includes('JSON')) {
      return 'Workspace data appears to be corrupted. You may need to clear your workspace data.';
    }

    // Generic error message
    return 'An unexpected error occurred. Please try again or clear your workspace data if the problem persists.';
  }
}

/**
 * Hook for using workspace error boundary in functional components
 */
export function useWorkspaceErrorHandler() {
  const handleError = (error: Error, context: string = 'workspace operation') => {
    console.error(`Error in ${context}:`, error);

    // Here you could integrate with error reporting services
    // like Sentry, LogRocket, etc.

    // For now, just log to console
    if (process.env.NODE_ENV === 'development') {
      console.trace('Error trace:', error);
    }
  };

  return { handleError };
}

export default WorkspaceErrorBoundary;
